import { apiRequest } from "./apiClient";

export const DEFAULT_NOTIFICATION_SETTINGS = {
  pushEnabled: true,
  nativeEnabled: true,
  inAppEnabled: true,
  chat: true,
  tasks: true,
  calendar: true,
  health: true,
  shopping: true,
  shoppingReminderEnabled: true,
  sharedExpenses: true,
  finance: true,
  documents: true,
  documentExpiry: true,
  familyAnnouncements: true,
  birthdayNotifications: true,
  familyStatus: true,
  location: true,
  quietHoursEnabled: false,
  quietStart: "22:00",
  quietEnd: "08:00",
  shoppingReminder:  { enabled: true, frequency: "daily", scope: "family" },
  taskReminder:      { today: true, tomorrow: true, threeDaysBefore: true },
  calendarReminder:  { today: true, tomorrow: true },
  documentReminder:  { oneDayBefore: true, sevenDaysBefore: true, thirtyDaysBefore: true },
  healthReminder:    { appointments: true, medicines: true },
  financeReminder:   { installments: true, subscriptions: true, bills: true, savings: true },
  categoryPreferences: null,
  reminderPreferences: null,
};

export const NOTIFICATION_CATEGORY_KEYS = [
  "chat", "tasks", "calendar", "health", "shopping", "sharedExpenses",
  "finance", "documents", "documentExpiry", "familyAnnouncements", "birthdayNotifications", "familyStatus", "location",
];

const categorySnapshot = (settings) => Object.fromEntries(
  NOTIFICATION_CATEGORY_KEYS.map((key) => [key, settings?.[key] !== false])
);

const reminderSnapshot = (settings) => ({
  shoppingReminderEnabled: settings?.shoppingReminderEnabled !== false,
  shoppingReminder: {
    ...DEFAULT_NOTIFICATION_SETTINGS.shoppingReminder,
    ...(settings?.shoppingReminder || {}),
  },
  taskReminder: {
    ...DEFAULT_NOTIFICATION_SETTINGS.taskReminder,
    ...(settings?.taskReminder || {}),
  },
  calendarReminder: {
    ...DEFAULT_NOTIFICATION_SETTINGS.calendarReminder,
    ...(settings?.calendarReminder || {}),
  },
  documentReminder: {
    ...DEFAULT_NOTIFICATION_SETTINGS.documentReminder,
    ...(settings?.documentReminder || {}),
  },
  healthReminder: {
    ...DEFAULT_NOTIFICATION_SETTINGS.healthReminder,
    ...(settings?.healthReminder || {}),
  },
  financeReminder: {
    ...DEFAULT_NOTIFICATION_SETTINGS.financeReminder,
    ...(settings?.financeReminder || {}),
  },
});

const allFalse = (object) => Object.fromEntries(Object.keys(object).map((key) => [key, false]));

export function disableAllNotificationSettings(settings = DEFAULT_NOTIFICATION_SETTINGS) {
  const snapshot = settings.pushEnabled !== false
    ? categorySnapshot(settings)
    : (settings.categoryPreferences || categorySnapshot(settings));
  const reminders = settings.pushEnabled !== false
    ? reminderSnapshot(settings)
    : (settings.reminderPreferences || reminderSnapshot(settings));
  return {
    ...settings,
    pushEnabled: false,
    nativeEnabled: false,
    ...Object.fromEntries(NOTIFICATION_CATEGORY_KEYS.map((key) => [key, false])),
    categoryPreferences: snapshot,
    shoppingReminderEnabled: false,
    shoppingReminder: { ...reminders.shoppingReminder, enabled: false, frequency: "off" },
    taskReminder: allFalse(reminders.taskReminder),
    calendarReminder: allFalse(reminders.calendarReminder),
    documentReminder: allFalse(reminders.documentReminder),
    healthReminder: allFalse(reminders.healthReminder),
    financeReminder: allFalse(reminders.financeReminder),
    reminderPreferences: reminders,
  };
}

export function enableAllNotificationSettings(settings = DEFAULT_NOTIFICATION_SETTINGS) {
  const snapshot = settings.categoryPreferences || categorySnapshot(DEFAULT_NOTIFICATION_SETTINGS);
  const reminders = settings.reminderPreferences || reminderSnapshot(DEFAULT_NOTIFICATION_SETTINGS);
  return {
    ...settings,
    pushEnabled: true,
    nativeEnabled: true,
    ...Object.fromEntries(NOTIFICATION_CATEGORY_KEYS.map((key) => [key, snapshot[key] !== false])),
    categoryPreferences: snapshot,
    ...reminders,
    reminderPreferences: reminders,
  };
}

// Per-user cache — aynı oturumda tekrar GET atma
const settingsCache = new Map();

// ─── Okuma ────────────────────────────────────────────────────────────────────
export async function getUserNotificationSettings(familyId, uid) {
  return getNotificationSettings(familyId, uid, uid);
}

export async function getMyNotificationSettings(familyId, uid) {
  return getNotificationSettings(familyId, uid, "me");
}

async function getNotificationSettings(familyId, uid, routeUid) {
  if (!uid) return { ...DEFAULT_NOTIFICATION_SETTINGS };

  const cacheKey = `${familyId}:${uid}`;
  if (settingsCache.has(cacheKey)) return settingsCache.get(cacheKey);

  try {
    const data = await apiRequest(`/notifications/${familyId}/settings/${routeUid}`, {
      skipAndroidLiteHeader: true,
    });
    const raw  = data?.settings || {};

    // Kayıt yoksa default dön
    if (Object.keys(raw).length === 0) {
      const def = { ...DEFAULT_NOTIFICATION_SETTINGS };
      settingsCache.set(cacheKey, def);
      return def;
    }

    // Backend PUT sırasında `settings` kolonuna tüm flat objeyi JSON olarak kaydediyoruz.
    // GET ise module bazlı döndürüyor: { global: { settings: {...} }, chat: { settings: {...} }, ... }
    // global row'un settings alanından flat objeyi al
    const savedFlat = raw.global?.settings || {};

    const hasFlatKeys =
      "pushEnabled" in savedFlat ||
      "chat"        in savedFlat ||
      "tasks"       in savedFlat;

    let merged;

    if (hasFlatKeys) {
      // Tam flat obje kaydedilmiş — merge et
      merged = { ...DEFAULT_NOTIFICATION_SETTINGS, ...savedFlat };
      // Flat kayıtta kategori seçimleri doğrudan kendi boolean alanlarında tutulur.
      // Master açık olsa bile kapatılan bir kategori push üretmemelidir.
      merged.modulePush = Object.fromEntries(
        NOTIFICATION_CATEGORY_KEYS.map((key) => [key, merged.pushEnabled !== false && merged[key] !== false])
      );
    } else {
      // Eski kayıt ya da sadece module bazlı alanlar var
      const toBool = (v, def = true) => v === undefined || v === null ? def : !!v;

      const g = raw.global || {};
      merged = {
        ...DEFAULT_NOTIFICATION_SETTINGS,
        pushEnabled:    toBool(g.push_enabled   ?? g.pushEnabled),
        nativeEnabled:  toBool(g.native_enabled ?? g.nativeEnabled),
        inAppEnabled:   toBool(g.in_app_enabled ?? g.inAppEnabled),
        quietHoursEnabled: !!(g.quiet_hours_start || g.quietStart),
        quietStart: g.quiet_hours_start || g.quietStart || "22:00",
        quietEnd:   g.quiet_hours_end   || g.quietEnd   || "08:00",

        // NOT: Bu alanlar (chat/tasks/calendar/...) UI'da "in-app bildirim merkezinde
        // göster" anlamında kullanılıyor — bilerek in_app_enabled'dan okunuyor,
        // BU DAVRANIŞI DEĞİŞTİRMİYORUZ.
        chat:           toBool((raw.chat           || {}).in_app_enabled ?? (raw.chat           || {}).inAppEnabled),
        tasks:          toBool((raw.tasks          || {}).in_app_enabled ?? (raw.tasks          || {}).inAppEnabled),
        calendar:       toBool((raw.calendar       || {}).in_app_enabled ?? (raw.calendar       || {}).inAppEnabled),
        health:         toBool((raw.health         || {}).in_app_enabled ?? (raw.health         || {}).inAppEnabled),
        shopping:       toBool((raw.shopping       || {}).in_app_enabled ?? (raw.shopping       || {}).inAppEnabled),
        sharedExpenses: toBool((raw.sharedExpenses || {}).in_app_enabled ?? (raw.sharedExpenses || {}).inAppEnabled),
        finance:        toBool((raw.finance        || {}).in_app_enabled ?? (raw.finance        || {}).inAppEnabled),
        documents:      toBool((raw.documents      || {}).in_app_enabled ?? (raw.documents      || {}).inAppEnabled),
        familyAnnouncements: toBool((raw.familyAnnouncements || {}).in_app_enabled ?? (raw.familyAnnouncements || {}).inAppEnabled),
        birthdayNotifications: savedFlat.birthdayNotifications !== false,
        familyStatus:   toBool((raw.familyStatus   || {}).in_app_enabled ?? (raw.familyStatus   || {}).inAppEnabled),
        location:       toBool((raw.location       || {}).in_app_enabled ?? (raw.location       || {}).inAppEnabled),

        // YENİ: modulePush — gerçek PUSH kararı için modülün kendi push_enabled'ı.
        // bildirimGonderilebilirMi() artık settings[settingKey] yerine bunu kullanıyor.
        // Önceden settings.tasks (in_app_enabled) push kararı için yanlışlıkla
        // kullanılıyordu; bu, in-app bildirimi kapalı olan bir kullanıcının push'unu
        // da yanlışlıkla engelliyordu (push_enabled açık olsa bile).
        modulePush: {
          chat:           toBool((raw.chat           || {}).push_enabled ?? (raw.chat           || {}).pushEnabled),
          tasks:          toBool((raw.tasks          || {}).push_enabled ?? (raw.tasks          || {}).pushEnabled),
          calendar:       toBool((raw.calendar       || {}).push_enabled ?? (raw.calendar       || {}).pushEnabled),
          health:         toBool((raw.health         || {}).push_enabled ?? (raw.health         || {}).pushEnabled),
          shopping:       toBool((raw.shopping       || {}).push_enabled ?? (raw.shopping       || {}).pushEnabled),
          sharedExpenses: toBool((raw.sharedExpenses || {}).push_enabled ?? (raw.sharedExpenses || {}).pushEnabled),
          finance:        toBool((raw.finance        || {}).push_enabled ?? (raw.finance        || {}).pushEnabled),
          documents:      toBool((raw.documents      || {}).push_enabled ?? (raw.documents      || {}).pushEnabled),
          familyAnnouncements: toBool((raw.familyAnnouncements || {}).push_enabled ?? (raw.familyAnnouncements || {}).pushEnabled),
          birthdayNotifications: savedFlat.birthdayNotifications !== false,
          familyStatus:   toBool((raw.familyStatus   || {}).push_enabled ?? (raw.familyStatus   || {}).pushEnabled),
          location:       toBool((raw.location       || {}).push_enabled ?? (raw.location       || {}).pushEnabled),
        },
      };
    }

    settingsCache.set(cacheKey, merged);
    return merged;
  } catch (err) {
    console.warn("[getUserNotificationSettings] default kullanılıyor:", err);
    return { ...DEFAULT_NOTIFICATION_SETTINGS };
  }
}

// ─── Kaydetme ─────────────────────────────────────────────────────────────────
// Backend PUT handler tam flat settings bekliyor:
//   settings.pushEnabled && settings[module]  →  push_enabled
//   settings.inAppEnabled                     →  in_app_enabled
//   settings.quietStart/End                   →  quiet_hours_start/end
//   JSON.stringify(settings)                  →  settings kolonu (tüm flat obje)
export async function saveUserNotificationSettings(familyId, uid, settings) {
  return saveNotificationSettings(familyId, uid, uid, settings);
}

export async function saveMyNotificationSettings(familyId, uid, settings) {
  return saveNotificationSettings(familyId, uid, "me", settings);
}

async function saveNotificationSettings(familyId, uid, routeUid, settings) {
  if (!uid) throw new Error("Kullanıcı UID gerekli.");

  // Backend'e tam flat objeyi gönder — settings kolonu bunu saklıyor
  const flatSettings = {
    pushEnabled:    settings.pushEnabled    !== false,
    nativeEnabled:  settings.nativeEnabled  !== false,
    inAppEnabled:   settings.inAppEnabled   !== false,
    chat:           settings.chat           !== false,
    tasks:          settings.tasks          !== false,
    calendar:       settings.calendar       !== false,
    health:         settings.health         !== false,
    shopping:       settings.shopping       !== false,
    shoppingReminderEnabled: settings.shoppingReminderEnabled !== false,
    sharedExpenses: settings.sharedExpenses !== false,
    finance:        settings.finance        !== false,
    documents:      settings.documents      !== false,
    documentExpiry: settings.documentExpiry !== false,
    familyAnnouncements: settings.familyAnnouncements !== false,
    birthdayNotifications: settings.birthdayNotifications !== false,
    familyStatus:   settings.familyStatus   !== false,
    location:       settings.location       !== false,
    quietHoursEnabled: !!settings.quietHoursEnabled,
    quietStart: settings.quietHoursEnabled ? (settings.quietStart || "22:00") : null,
    quietEnd:   settings.quietHoursEnabled ? (settings.quietEnd   || "08:00") : null,
    // Alt tercihler — backend settings kolonunda saklanır
    shoppingReminder:  settings.shoppingReminder  || DEFAULT_NOTIFICATION_SETTINGS.shoppingReminder,
    taskReminder:      settings.taskReminder      || DEFAULT_NOTIFICATION_SETTINGS.taskReminder,
    calendarReminder:  settings.calendarReminder  || DEFAULT_NOTIFICATION_SETTINGS.calendarReminder,
    documentReminder:  settings.documentReminder  || DEFAULT_NOTIFICATION_SETTINGS.documentReminder,
    healthReminder:    settings.healthReminder    || DEFAULT_NOTIFICATION_SETTINGS.healthReminder,
    financeReminder:   settings.financeReminder   || DEFAULT_NOTIFICATION_SETTINGS.financeReminder,
    categoryPreferences: settings.categoryPreferences || null,
    reminderPreferences: settings.reminderPreferences || null,
  };

  await apiRequest(`/notifications/${familyId}/settings/${routeUid}`, {
    method: "PUT",
    body: JSON.stringify({ settings: flatSettings }),
    skipAndroidLiteHeader: true,
  });

  // Cache'i taze veriyle hemen güncelle (sonraki okuma GET atmaz)
  settingsCache.set(`${familyId}:${uid}`, { ...flatSettings });

  return true;
}

export function clearNotificationSettingsCache() {
  settingsCache.clear();
}
